import {
  Badge,
  Card,
  ChannelBadge,
  ChannelFilter,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
} from "@forge/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { useState } from "react";
import { ListSearch } from "../../components/list-search";
import { MessageThread } from "../../components/message-thread";
import { MobileInboxPane } from "../../components/mobile-inbox-pane";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { useChannels } from "../../lib/channel";
import { matchesListQuery } from "../../lib/list-query";
import { formatDateTime } from "../../lib/format";
import { isNarrowViewport } from "../../lib/mobile-title";
import { replyVarsFromOrder } from "../../lib/reply-templates";

type MessagesSearch = {
  awaiting?: string;
  accountId?: string;
  provider?: "mercadolivre" | "shopee";
  open?: string;
  q?: string;
};

export const Route = createFileRoute("/_app/messages")({
  validateSearch: (search: Record<string, unknown>): MessagesSearch => ({
    awaiting: typeof search.awaiting === "string" ? search.awaiting : "true",
    accountId:
      typeof search.accountId === "string" ? search.accountId : undefined,
    provider:
      search.provider === "mercadolivre" || search.provider === "shopee"
        ? search.provider
        : undefined,
    open: typeof search.open === "string" ? search.open : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
  }),
  component: MessagesPage,
});

function MessagesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });
  const channels = useChannels();

  const inbox = useQuery({
    queryKey: ["messages", search],
    queryFn: async () =>
      unwrap(
        await api.messages.get({
          query: {
            awaiting: search.awaiting === "all" ? undefined : "true",
            accountId: search.accountId,
            provider: search.provider,
          },
        }),
      ),
  });

  const allThreads = inbox.data?.threads ?? [];
  const threads = allThreads.filter((row) =>
    matchesListQuery(search.q, [row.buyerName, row.externalOrderId]),
  );
  const writesEnabled = inbox.data?.writesEnabled ?? false;
  const selected =
    threads.find((row) => row.orderId && row.orderId === search.open) ?? null;

  function closeThread() {
    void navigate({
      search: (prev) => ({ ...prev, open: undefined }),
    });
  }

  return (
    <>
      <PageHeader
        title="Mensagens"
        description={`${threads.length} conversas ${search.awaiting === "all" ? "" : "aguardando resposta"}`}
        actions={
          <>
            <ListSearch
              value={search.q ?? ""}
              placeholder="Comprador ou pedido"
              onValueChange={(value) =>
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    q: value.trim() || undefined,
                  }),
                })
              }
            />
            <Select
              value={search.awaiting ?? "true"}
              itemToStringLabel={(value) =>
                value === "all" ? "Todas" : "Aguardando resposta"
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    awaiting: (value as string) ?? "true",
                  }),
                })
              }
            >
              <SelectTrigger />
              <SelectContent>
                <SelectItem value="true">Aguardando resposta</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={search.accountId ?? null}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todas as contas"
                  : (accounts.data?.accounts.find((account) => account.id === value)
                      ?.displayName ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    accountId: (value as string) ?? undefined,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todas as contas" />
              <SelectContent>
                <SelectItem value={null}>Todas as contas</SelectItem>
                {accounts.data?.accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <ChannelFilter
        className="mb-3"
        value={search.provider}
        channels={channels.providers.map((provider) => ({ provider }))}
        onChange={(provider) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              provider:
                provider === "mercadolivre" || provider === "shopee"
                  ? provider
                  : undefined,
            }),
          })
        }
      />

      {inbox.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : allThreads.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquare />}
            title="Nenhuma mensagem"
            description="Importe as mensagens em Conexões ou aguarde o aviso do canal."
          />
        </Card>
      ) : threads.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MessageSquare />}
            title="Nenhuma conversa nesta busca"
            description="Tente o nome do comprador ou o código do pedido."
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {threads.map((row) => {
              const key = row.orderId ?? `pack:${row.externalPackId}`;
              const open = Boolean(row.orderId && openOrderId === row.orderId);
              return (
                <li key={key}>
                  {row.orderId ? (
                    <button
                      type="button"
                      className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left active:scale-[0.99] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken"
                      onClick={() => {
                        if (isNarrowViewport()) {
                          void navigate({
                            search: (prev) => ({
                              ...prev,
                              open: row.orderId ?? undefined,
                            }),
                          });
                          return;
                        }
                        setOpenOrderId((current) =>
                          current === row.orderId ? null : row.orderId,
                        );
                      }}
                    >
                      <ThreadPreview row={row} />
                    </button>
                  ) : (
                    <div className="px-4 py-3">
                      <ThreadPreview row={row} />
                    </div>
                  )}
                  {open && row.orderId ? (
                    <div className="hidden border-t border-line bg-surface-sunken/40 p-4 md:block">
                      <div className="mb-3">
                        <Link
                          to="/orders/$id"
                          params={{ id: row.orderId }}
                          className="text-2xs font-medium text-ember-strong hover:underline"
                        >
                          Abrir pedido #{row.externalOrderId ?? "…"}
                        </Link>
                      </div>
                      <ThreadBody
                        orderId={row.orderId}
                        writesEnabled={writesEnabled}
                        onSent={() => {
                          void queryClient.invalidateQueries({
                            queryKey: ["messages"],
                          });
                        }}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <MobileInboxPane
        open={Boolean(search.open)}
        title={
          selected?.buyerName ??
          (selected?.externalOrderId
            ? `#${selected.externalOrderId}`
            : "Conversa")
        }
        subtitle={
          selected?.orderId ? (
            <Link
              to="/orders/$id"
              params={{ id: selected.orderId }}
              className="text-ember-strong"
            >
              Pedido #{selected.externalOrderId ?? "…"}
            </Link>
          ) : (
            selected?.accountNickname
          )
        }
        onClose={closeThread}
      >
        {search.open ? (
          <ThreadBody
            orderId={search.open}
            writesEnabled={writesEnabled}
            variant="sheet"
            onSent={() => {
              void queryClient.invalidateQueries({
                queryKey: ["messages"],
              });
            }}
          />
        ) : null}
      </MobileInboxPane>
    </>
  );
}

function ThreadPreview({
  row,
}: {
  row: {
    externalOrderId: string | null;
    buyerName: string | null;
    lastText: string;
    lastAt: string | Date;
    awaitingReply: boolean;
    messageCount: number;
    accountNickname?: string;
    accountProvider?: string;
  };
}) {
  return (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-medium text-ink">
          {row.buyerName ??
            (row.externalOrderId ? `#${row.externalOrderId}` : "Conversa")}
        </p>
        <p className="mt-0.5 truncate text-2xs text-ink-muted">{row.lastText}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-2xs text-ink-faint">
          {row.accountProvider ? (
            <ChannelBadge
              provider={row.accountProvider}
              nickname={row.accountNickname}
            />
          ) : (
            row.accountNickname
          )}
          <span>· {formatDateTime(row.lastAt)} · {row.messageCount} msg</span>
        </p>
      </div>
      {row.awaitingReply && (
        <Badge variant="warning" dot>
          Sem resposta
        </Badge>
      )}
    </>
  );
}

function ThreadBody({
  orderId,
  writesEnabled,
  onSent,
  variant = "inline",
}: {
  orderId: string;
  writesEnabled: boolean;
  onSent: () => void;
  variant?: "inline" | "sheet";
}) {
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["orders", orderId],
    queryFn: async () => unwrap(await api.orders({ id: orderId }).get()),
  });
  const order = detail.data && "order" in detail.data ? detail.data.order : null;
  const messages = order && "messages" in order ? order.messages : [];

  if (detail.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="size-4 text-ink-muted" />
      </div>
    );
  }
  if (detail.isError || !order) {
    return (
      <p className="px-4 py-6 text-2xs text-ink-muted">
        Não foi possível carregar esta conversa. O pedido pode ter saído da
        importação ou o canal já apagou o histórico.
      </p>
    );
  }

  return (
    <MessageThread
      variant={variant}
      orderId={orderId}
      messages={messages}
      writesEnabled={
        "writesEnabled" in order ? Boolean(order.writesEnabled) : writesEnabled
      }
      vars={replyVarsFromOrder(order)}
      onSent={() => {
        void queryClient.invalidateQueries({ queryKey: ["orders", orderId] });
        onSent();
      }}
    />
  );
}
